import { ActionIcon, Group, Loader, ScrollArea, Text, Tooltip } from "@mantine/core";
import { useInfiniteQuery, useQueryClient } from "@tanstack/react-query";
import { Smile, Trash2 } from "lucide-react";
import { useState } from "react";

import {
  getListActorsQueryKey,
  listActors,
  useDeleteActor,
} from "../../../api/generated/endpoints/generation/generation";
import { useListMembers } from "../../../api/generated/endpoints/teams/teams";
import type { AiActor, AiActorGender, ListActorsParams } from "../../../api/generated/model";
import { refreshTeamStorage } from "../../../api/team-storage";
import { ErrorAlert } from "../../../platform/ErrorAlert";
import { portraitItem } from "../../../platform/actors/actor-preview";
import { GENDERS } from "../../../platform/actors/genders";
import { useCreateTalkingHead } from "../../../platform/actors/talking-head-handoff";
import { CreatorFilter } from "../../../platform/media/CreatorFilter";
import { ProductFilter } from "../../../platform/media/ProductFilter";
import { LIBRARY_ORDERS } from "../../../platform/media/library-pages";
import { DetailSection, MediaPreview } from "../../../platform/media/MediaPreview";
import { PreviewMaker } from "../../../platform/media/PreviewMaker";
import { memberName } from "../../../platform/members";
import { useMembership } from "../../../platform/membership";
import { canEditContent } from "../../../platform/permissions";
import { readPref, writePref } from "../../../platform/prefs";
import { ColumnsSetting } from "../../../ui/ColumnsSetting";
import { ConfirmModal } from "../../../ui/ConfirmModal";
import { EmptyState } from "../../../ui/EmptyState";
import { FilterMenu } from "../../../ui/FilterMenu";
import { MasonryGrid } from "../../../ui/MasonryGrid";
import { SortMenu } from "../../../ui/SortMenu";
import { SubmitSearch } from "../../../ui/SubmitSearch";
import { LoadAhead, morePages } from "../../../ui/LoadAhead";
import classes from "./actors.module.css";
import type { ActorsQuery } from "./GlobalActors";
import { ActorMenu, MyActorCard } from "./MyActorCard";
import { markUnsaved } from "./saved-portraits";

/** Live's page size and column counts, as on Global Actors. */
const PAGE = 24;
const COLUMN_MARKS = [2, 4, 6, 8, 10] as const;
/** Live's search box takes 100 characters, as the route does. */
const SEARCH_MAX = 100;

export interface MyActorsProps {
  teamId: string;
  brandId: string;
  /** The page's search and order, shared with Global Actors as live's page context. */
  query: ActorsQuery;
  onQuery: (change: Partial<ActorsQuery>) => void;
  /** My Actors' own Gender, kept while another tab shows. */
  gender: AiActorGender | undefined;
  onGender: (gender: AiActorGender | undefined) => void;
  /** The creators picked on live's chips; none is everyone ("All"). */
  creators: readonly string[];
  onCreators: (userIds: string[]) => void;
  /** Live's Product: one of the team's products, kept while another tab shows. */
  product: string | undefined;
  onProduct: (productId: string | undefined) => void;
  /** "Create a Talking Head": the Studio's Talking Head unless the page opens its own. */
  onTalkingHead?: ((actor: AiActor) => void) | undefined;
  /** At most this many columns, whatever the saved setting (the redesign's page on a phone). */
  columnsAtMost?: number | undefined;
}

/**
 * Live's My Actors tab (`tabs/my-actors`, captures 01, 02, L04 to L04c, G8-team-actors): the
 * team's own actors, from every brand, newest first, 24 at a time; live's search, creator chips,
 * Gender, Product (the actors saved from a generation that showed it, #237) and sort over them.
 * A tile deletes after live's confirmation, tells who made it and when, and downloads; the card
 * opens the portrait in the shared lightbox (issue 828, bug 25, Daniil's request beyond live's
 * preview, whose arrows and strip are his): the list's actors, paging as the list does, with
 * live's preview header (the maker's avatar, name and day, no title), its Delete, ⓘ and "…".
 */
export function MyActors(props: MyActorsProps) {
  const { teamId, brandId, query, onQuery, gender, onGender, creators, onCreators, product, onProduct } =
    props;
  const queryClient = useQueryClient();
  const me = useMembership(teamId);
  const [columns, setColumns] = useState(() => readPref("actorColumns"));
  // The actor the lightbox shows, by id; null while it is closed.
  const [previewing, setPreviewing] = useState<string | null>(null);
  const [deleting, setDeleting] = useState<AiActor | null>(null);
  const { columnsAtMost } = props;
  const studioTalkingHead = useCreateTalkingHead();
  const createTalkingHead = props.onTalkingHead ?? studioTalkingHead;
  const params: ListActorsParams = {
    kind: "team",
    ...(query.search === "" ? {} : { search: query.search }),
    ...(gender === undefined ? {} : { gender: [gender] }),
    ...(creators.length === 0 ? {} : { created_by: [...creators] }),
    ...(product === undefined ? {} : { product_id: product }),
    ...(query.order === "newest" ? {} : { order: query.order }),
  };
  const actors = useInfiniteQuery({
    queryKey: [...getListActorsQueryKey(teamId, brandId, params), "pages"],
    queryFn: ({ pageParam, signal }) =>
      listActors(
        teamId,
        brandId,
        { ...params, limit: PAGE, ...(pageParam === undefined ? {} : { after: pageParam }) },
        { signal },
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.next ?? undefined,
  });
  const remove = useDeleteActor({
    mutation: {
      onSuccess: async (_, { actorId }) => {
        setDeleting(null);
        // Its portrait is not saved any more (issue 407): My Generations offers Save again.
        markUnsaved(queryClient, teamId, actorId);
        // Its portrait's bytes are given back to the team's storage.
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [getListActorsQueryKey(teamId, brandId)[0]] }),
          refreshTeamStorage(queryClient, teamId),
        ]);
      },
    },
  });
  const items = actors.data?.pages.flatMap((page) => page.items) ?? [];
  // Who made each actor, for the lightbox's header: read once it opens, from the page's cache
  // where the creator chips hold them.
  const members = useListMembers(teamId, {
    query: { enabled: previewing !== null, staleTime: Number.POSITIVE_INFINITY },
  }).data;
  const actorOf = (id: string) => items.find((each) => each.id === id);
  const maker = (actor: AiActor) =>
    actor.created_by === null
      ? "Unknown"
      : memberName(members?.find((member) => member.user_id === actor.created_by));
  const mayDelete = (actor: AiActor) =>
    me !== undefined && canEditContent(me.role, actor.created_by ?? "", me.userId);
  return (
    <div className={classes.tab}>
      <div className={classes.toolbar}>
        <Group gap="sm" wrap="nowrap">
          <SubmitSearch
            label="Search by actor name or partial match"
            value={query.search}
            maxLength={SEARCH_MAX}
            onSearch={(search) => onQuery({ search, typed: search })}
          />
          <CreatorFilter teamId={teamId} value={creators} onChange={onCreators} />
          <FilterMenu
            label="Gender"
            icon={<Smile size={16} />}
            options={GENDERS}
            value={gender}
            onChange={onGender}
          />
          <ProductFilter teamId={teamId} value={product} onChange={onProduct} />
        </Group>
        <Group gap={4} wrap="nowrap">
          <SortMenu
            options={LIBRARY_ORDERS}
            value={query.order}
            onChange={(order) => onQuery({ order })}
          />
          <ColumnsSetting
            value={columns}
            marks={COLUMN_MARKS}
            onChange={(next) => {
              setColumns(next);
              writePref("actorColumns", next);
            }}
          />
        </Group>
      </div>
      {/* The list scrolls on its own under the toolbar, which stays (live's `TabLayout`). */}
      <ScrollArea type="auto" offsetScrollbars className={classes.list}>
        {actors.isPending ? <Loader size="sm" aria-label="Loading the actors" /> : null}
        {actors.isError ? <ErrorAlert error={actors.error} /> : null}
        {actors.isSuccess && items.length === 0 ? (
          <EmptyState title="No actors yet" description="Create your first AI actor to get started." />
        ) : null}
        {items.length > 0 ? (
          <MasonryGrid columns={Math.min(columns, columnsAtMost ?? columns)} label="My Actors">
            {items.map((actor) => (
              <MyActorCard
                key={actor.id}
                teamId={teamId}
                actor={actor}
                mayDelete={mayDelete(actor)}
                onDelete={() => setDeleting(actor)}
                onPreview={() => setPreviewing(actor.id)}
                onTalkingHead={() => createTalkingHead(actor)}
              />
            ))}
          </MasonryGrid>
        ) : null}
        {/* Live's: the next page as the end shows (`InfiniteScroll`), no button. */}
        <LoadAhead more={morePages(actors)} loaded={items.length} label="Loading more actors" />
      </ScrollArea>
      <MediaPreview
        items={items.map(portraitItem)}
        current={previewing}
        onShow={setPreviewing}
        more={morePages(actors)}
        paused={deleting !== null}
        header={(item) => {
          const actor = actorOf(item.id);
          return actor === undefined ? null : <PreviewMaker name={maker(actor)} at={actor.created_at} />;
        }}
        actions={(item) => {
          const actor = actorOf(item.id);
          if (actor === undefined) return null;
          return (
            <>
              {mayDelete(actor) ? (
                <Tooltip label="Delete">
                  <ActionIcon
                    variant="subtle"
                    color="gray"
                    c="var(--cai-color-viewer-text)"
                    aria-label={`Delete ${actor.name}`}
                    onClick={() => setDeleting(actor)}
                  >
                    <Trash2 size={18} aria-hidden />
                  </ActionIcon>
                </Tooltip>
              ) : null}
              <ActorMenu
                actor={actor}
                onTalkingHead={() => createTalkingHead(actor)}
                trigger={{ variant: "subtle", color: "gray", c: "var(--cai-color-viewer-text)" }}
              />
            </>
          );
        }}
        // Live's ⓘ on an actor: "Info", and no more to say (`AssetModalInfo` with no prompt).
        details={() => (
          <DetailSection title="Info">
            <Text size="sm" c="var(--cai-color-viewer-muted)">
              No additional information available
            </Text>
          </DetailSection>
        )}
      />
      <ConfirmModal
        opened={deleting !== null}
        title={`Delete avatar “${deleting?.name ?? ""}”?`}
        confirmLabel="Delete"
        onConfirm={() => {
          if (deleting !== null) remove.mutate({ teamId, actorId: deleting.id });
        }}
        onCancel={() => {
          remove.reset();
          setDeleting(null);
        }}
        pending={remove.isPending}
        error={remove.isError ? <ErrorAlert error={remove.error} /> : null}
      >
        <Text size="sm">This action cannot be undone. The avatar will be removed from the library.</Text>
      </ConfirmModal>
    </div>
  );
}
